import { defineStore } from 'pinia'

import axios from '@axios'
import { reportError } from '@/plugins/misc'
import localforage from 'localforage'

const baseUrl = `/reservation/`

var listeningMyReservations = false
var listeningConfirming = false

// Request deduplication: track pending requests to prevent duplicate calls
const pendingJustBookedRequests = new Map()

export const useReservationsStore = defineStore({
  id: 'reservations',
  state: () => ({
    // the server's refusal of the last delete (409), for the card to show; null otherwise
    deleteRefusal: null,
    // RM-359: the server's Pending RM Review notice for the request just
    // submitted ({ reservationId, text }), shown once where the member lands.
    lastApprovalNotice: null,
    myReservations: [], //JSON.parse(localStorage.getItem('myReservations')),
    completeReservations: [], //JSON.parse(localStorage.getItem('completeReservations')),
    inReviewReservations: [], //JSON.parse(localStorage.getItem('inReviewReservations')),
    justBookedReservations: [], //JSON.parse(localStorage.getItem('justBookedReservations')),
    justBookedReservationsByRestaurant: {},
    restaurantStatsByRestaurant: {},
    editingReservation: null,
    viewingReservation: null,
    cancelingReservation: null,
    confirmingReservation: null,
    confirmingReservationUuid: null,
    // RM-360: the member's preview link shows a restaurant's response page
    // read-only — nothing on it saves.
    confirmingIsPreview: false,
    fetching: false,
    saving: false,
    isInitialized: false,
    lastCompleteStatus: null,
    completedAnalytics: null,
    analyticsLoading: false,
    openAtPaymentStep: false,
    openAtStep: null,
    insights: null,
    insightsLoading: false,

    // Presence / member-commitment sign-off captured on the Host Profile step
    // (7b): "this request is for me, I'll be present & I agree to the member
    // commitment." The final Confirm & Pay step (7c) gates Submit on it.
    confirmedPresence: false,

    // Bumped whenever a reservation_status_changed event is received over the
    // /realtime socket. Pages that show stale-sensitive lists (e.g. the
    // reservations-review dashboard) watch this and re-fetch.
    lastStatusChangeNotifiedAt: 0,
    lastStatusChangeReservationId: null,
    lastEarliestDate: null,
    lastLatestDate: null,

    // RM-154: aggregate YTD acceptance rate across Preferred restaurants,
    // displayed on the reservation request flow's intermediary guidance screen.
    // Lazily populated by getPreferredAcceptanceRateYtd(); reused across opens.
    preferredAcceptanceRateYtd: null,
    preferredAcceptanceRateYtdFetchedAt: 0,
    preferredAcceptanceRateYtdLoading: false,
  }),

  actions: {
    async loadInitialData() {
      if (this.isInitialized) {
        return  // If already initialized, do nothing
      }

      const myReservations = await localforage.getItem('myReservations')
      const inReviewReservations = await localforage.getItem('inReviewReservations')
      const justBookedReservations = await localforage.getItem('justBookedReservations')

      // Clean up stale completeReservations cache (no longer used, was causing unbounded growth)
      localforage.removeItem('completeReservations')

      // const justBookedReservationsByRestaurant = await localforage.getItem('justBookedReservationsByRestaurant');
      if (myReservations) {
        this.myReservations = JSON.parse(myReservations)
      }
      if (inReviewReservations) {
        this.inReviewReservations = JSON.parse(inReviewReservations)
      }
      if (justBookedReservations) {
        this.justBookedReservations = JSON.parse(justBookedReservations)
      }

      // if (justBookedReservationsByRestaurant) {
      //     this.justBookedReservationsByRestaurant = JSON.parse(justBookedReservationsByRestaurant);
      // }

      this.isInitialized = true
    },

    async getMyReservations() {
      //this.myReservation = { loading: true };
      this.fetching = true
      try {
        let res = await axios.get(baseUrl, {
          params: {
            mine: true
          }
        })
        this.myReservations = res.data
        localforage.setItem('myReservations', JSON.stringify(this.myReservations))
        this.fetching = false
      } catch (error) {
        console.error(error)
        this.fetching = false
        reportError('reservations.store.js:getMyReservations', error.message, null, null, null, error)
      }
    },

    async getCompleteReservations(status, earliestDate, latestDate) {
      this.fetching = true
      try {
        const params = {
          status: status,
          earliestDate: earliestDate
        }
        if (latestDate) params.latestDate = latestDate

        let res = await axios.get(baseUrl, { params })

        // Merge the new data with the existing data
        this.completeReservations = [...this.completeReservations, ...res.data]

        // Remove duplicates based on _id (last write wins)
        this.completeReservations = [...new Map(this.completeReservations.map(item => [item._id, item])).values()]
        this.fetching = false
        this.lastCompleteStatus = status
        this.lastEarliestDate = earliestDate
        this.lastLatestDate = latestDate
      } catch (error) {
        console.error(error)
        this.fetching = false
        reportError('reservations.store.js:getCompleteReservations', error.message, null, null, null, error)
      }
    },

    async getAllCompleteReservations(earliestDate, latestDate) {
      this.fetching = true
      try {
        const params = {
          completed: true,
          earliestDate: earliestDate
        }
        if (latestDate) params.latestDate = latestDate

        const res = await axios.get(baseUrl, { params })

        // Atomic swap -- do NOT clear completeReservations before fetch
        // to avoid destroying reactive references and crashing mounted components
        this.completeReservations = res.data
        this.lastEarliestDate = earliestDate
        this.lastLatestDate = latestDate
      } catch (error) {
        console.error(error)
        reportError('reservations.store.js:getAllCompleteReservations', error.message, null, null, null, error)
      } finally {
        this.fetching = false
      }
    },

    async getCompletedAnalytics(earliestDate, latestDate) {
      this.analyticsLoading = true
      try {
        const params = {
          earliestDate: earliestDate,
        }
        if (latestDate) params.latestDate = latestDate

        const res = await axios.get(`${baseUrl}analytics`, { params })
        this.completedAnalytics = res.data
      } catch (error) {
        console.error(error)
        reportError('reservations.store.js:getCompletedAnalytics', error.message, null, null, null, error)
      } finally {
        this.analyticsLoading = false
      }
    },

    async getPreferredAcceptanceRateYtd({ forceRefresh = false } = {}) {
      // RM-154: cached for 1h client-side; the backend also caches for 1h, so
      // the typical hit fills the value into state without going to the wire.
      const TTL_MS = 60 * 60 * 1000
      const fresh = this.preferredAcceptanceRateYtd
        && (Date.now() - this.preferredAcceptanceRateYtdFetchedAt) < TTL_MS
      if (fresh && !forceRefresh) return this.preferredAcceptanceRateYtd
      if (this.preferredAcceptanceRateYtdLoading) return this.preferredAcceptanceRateYtd

      this.preferredAcceptanceRateYtdLoading = true
      try {
        const res = await axios.get(`${baseUrl}preferred-acceptance-rate-ytd`, {
          params: {},
        })
        this.preferredAcceptanceRateYtd = res.data
        this.preferredAcceptanceRateYtdFetchedAt = Date.now()

        return this.preferredAcceptanceRateYtd
      } catch (error) {
        console.error('getPreferredAcceptanceRateYtd', error)
        reportError('reservations.store.js:getPreferredAcceptanceRateYtd', error.message, null, null, null, error)

        return null
      } finally {
        this.preferredAcceptanceRateYtdLoading = false
      }
    },

    async getInReviewReservations() {
      //this.myReservation = { loading: true };
      this.fetching = true
      try {
        let res = await axios.get(baseUrl, {
          params: {
            inReview: true
          }
        })
        this.inReviewReservations = res.data
        localforage.setItem('inReviewReservations', JSON.stringify(this.inReviewReservations))
        this.fetching = false
      } catch (error) {
        console.error(error)
        this.fetching = false  // Fix: Reset fetching on error
        reportError('reservations.store.js:getInReviewReservations', error.message, null, null, null, error)
      }
    },

    async getReservationById(reservationId) {
      if (!this.fetching) {
        try {
          let res = await axios.get(`${baseUrl}${reservationId}`, {
            params: {
            }
          })

          if (res.data) {
            let theReservation = this?.myReservations?.find(({ id }) => id === reservationId)
            if (theReservation) Object.assign(theReservation, res.data)
            let completeReservation = this?.completeReservations?.find(({ id }) => id === reservationId)
            if (completeReservation) Object.assign(completeReservation, res.data)
            let inReviewReservation = this?.inReviewReservations?.find(({ id }) => id === reservationId)
            if (inReviewReservation) Object.assign(inReviewReservation, res.data)

            if (this.editingReservation?.id == reservationId) Object.assign(this.editingReservation, res.data)
          }
        } catch (error) {
          console.log('Error dff3', error)
          reportError('reservations.store.js:getReservationById', error.message, null, null, null, error)
        }
      }
    },

    async getJustBookedReservationsByRestaurant(name, market, options = {}) {
      const { forceRefresh = false } = options
      const hasCachedReservations = this.justBookedReservationsByRestaurant[market]?.[name] !== undefined
      const hasCachedStats = this.restaurantStatsByRestaurant[market]?.[name] !== undefined

      // Check if already cached
      if (!forceRefresh && hasCachedReservations && hasCachedStats) {
        return this.justBookedReservationsByRestaurant[market][name]
      }

      // Check if request is already pending (deduplication)
      const requestKey = `${market}:${name}`
      if (pendingJustBookedRequests.has(requestKey)) {
        return pendingJustBookedRequests.get(requestKey)
      }

      // Create new request
      const requestPromise = (async () => {
        try {
          let res = await axios.get(baseUrl, {
            params: {
              justBookedRestaurant: name,
              justBookedMarket: market
            }
          })
          const reservations = Array.isArray(res.data) ? res.data : (res.data?.reservations || [])
          const stats = Array.isArray(res.data) ? null : (res.data?.stats ?? null)

          if (!this.justBookedReservationsByRestaurant[market])
            this.justBookedReservationsByRestaurant[market] = {}
          if (!this.restaurantStatsByRestaurant[market])
            this.restaurantStatsByRestaurant[market] = {}

          this.justBookedReservationsByRestaurant[market][name] = reservations
          this.restaurantStatsByRestaurant[market][name] = stats
          
          return reservations
        } catch (error) {
          console.log('Error kF3')
          console.error(error)
          reportError('reservations.store.js:getJustBookedReservationsByRestaurant', error.message, null, null, null, error)
          throw error
        } finally {
          // Remove from pending requests
          pendingJustBookedRequests.delete(requestKey)
        }
      })()

      // Track pending request
      pendingJustBookedRequests.set(requestKey, requestPromise)
      
      return requestPromise
    },

    async getJustBookedReservations() {
      try {
        let res = await axios.get(baseUrl, {
          params: {
            justBooked: true
          }
        })
        this.justBookedReservations = res.data
        localforage.setItem('justBookedReservations', JSON.stringify(this.justBookedReservations))
      } catch (error) {
        console.log('Error k773')
        console.error(error)
        reportError('reservations.store.js:getJustBookedReservations', error.message, null, null, null, error)
      }
    },

    async getConfirmingReservation(uuid) {
      try {
        let res = await axios.get(baseUrl, {
          params: {
            confirmationUuid: uuid
          }
        })
        this.confirmingReservation = res.data
        this.confirmingReservationUuid = uuid
        this.confirmingIsPreview = false
      } catch (error) {
        console.error(error)
        alert('Sorry, there was an error loading this request. This link may no longer be ready')
        reportError('reservations.store.js:getConfirmingReservation', error.message, null, null, null, error)
      }
    },

    // RM-360: the member's request preview link — /referral/preview/<previewCode>.
    // Returns false when the link no longer works (the request was deleted).
    async getConfirmingReservationPreview(previewCode, restaurantId) {
      try {
        let res = await axios.get(baseUrl, {
          params: {
            previewCode,
            restaurantId: restaurantId || undefined,
          }
        })
        this.confirmingReservation = res.data
        this.confirmingReservationUuid = null
        this.confirmingIsPreview = true

        return true
      } catch (error) {
        this.confirmingReservation = null
        if (error?.response?.status !== 404) {
          console.error(error)
          reportError('reservations.store.js:getConfirmingReservationPreview', error.message, null, null, null, error)
        }

        return false
      }
    },

    async saveConfirmingReservation() {
      if (this.confirmingIsPreview) return
      this.saving = true
      try {
        let res = await axios.put(`${baseUrl}${this.confirmingReservation.id}/confirm`, this.confirmingReservation, {
          params: {
            confirmationUuid: this.confirmingReservationUuid
          },
          withCredentials: false  // This ensures cookies are not included with the request
        })
        Object.assign(this.confirmingReservation, res.data)
        this.saving = false
      } catch (error) {
        this.saving = false
        console.log('Error 3fssss3f', error)
        reportError('reservations.store.js:saveConfirmingReservation', error.message, null, null, null, error)
        alert('Sorry, someone else was responding to this reservation at the same time. Click okay to refresh the page and try again...')
        location.reload()
      }
    },

    async setupConfirmingListeners() {
      if (!listeningConfirming) {
        let that = this
        window.addEventListener('focus', function () {
          that.getConfirmingReservation(that.confirmingReservationUuid)
        })
        listeningConfirming = true
      }
    },

    //helps the reservations view stay up to date
    async setupMyReservationsListeners() {
      if (!listeningMyReservations) {
        let that = this
        this.myReservationsFocusListener = function () {
          if (!that.editingReservation) {
            console.log('FOCUS LISTENER - Getting my reservations')
            that.getMyReservations()
          }
        }
        window.addEventListener('focus', this.myReservationsFocusListener)
        listeningMyReservations = true
      }
    },

    removeMyReservationsListeners() {
      if (this.myReservationsFocusListener) {
        console.log('REMOVING LISTENER - My reservations')
        window.removeEventListener('focus', this.myReservationsFocusListener)

        // NULL ASSIGNMENT here would corrupt the pinia stores. RCA: Nulled Listener Error.md
        // this.myReservationsFocusListener = null;
        listeningMyReservations = false
      }
    },

    setupInReviewListeners() {
      // No-op: focus listeners removed
    },

    removeInReviewListeners() {
      // No-op: focus listeners removed
    },

    setupCompleteListeners() {
      // No-op: focus listeners removed
    },

    removeCompleteListeners() {
      // No-op: focus listeners removed
    },

    // Called by /realtime socket handler when the backend emits
    // reservation_status_changed. Bumps a reactive timestamp so pages can
    // watch it and trigger a refetch of their visible lists.
    handleReservationStatusChanged(payload) {
      this.lastStatusChangeReservationId = payload?.reservationId || null
      this.lastStatusChangeNotifiedAt = Date.now()
    },

    async newDraftReservation(userPermissions, createdBy, restaurantName, market, options = {}) {
      //this.myReservation = { loading: true };
      const reservationType = options.reservationType || 'general'
      let reservationToPost = {
        status: 'Draft',
        reservationType,
        requestExpirationTime: null,
        restaurantSpendShareSettings: []
      }
      if (userPermissions) {
        reservationToPost.userPermissions = userPermissions
      }
      if (createdBy) {
        reservationToPost.createdBy = createdBy
      }
      if (restaurantName && market) {
        reservationToPost.preferredRestaurant = restaurantName
        reservationToPost.market = market
      }
      if (localStorage.getItem('userData')) {
        try {
          let res = await axios.post(`${baseUrl}`, reservationToPost, {
            params: {
            }
          })

          // If userPermissions were provided (host was selected), fetch the full reservation
          // to populate the owner field with complete profile data before opening the dialog
          if (userPermissions && res.data.id) {
            try {
              let fullRes = await axios.get(`${baseUrl}${res.data.id}`, {
                params: {
                }
              })
              if (fullRes.data) {
                this.editingReservation = fullRes.data
              } else {
                this.editingReservation = res.data
              }
            } catch (fetchError) {
              // If fetching fails, use the lean reservation
              console.log('Could not fetch full reservation for owner population:', fetchError)
              this.editingReservation = res.data
            }
          } else {
            this.editingReservation = res.data
          }

          // Ensure these properties exist even if not returned from server
          if (!this.editingReservation.requestExpirationTime) {
            this.editingReservation.requestExpirationTime = null
          }
          if (!this.editingReservation.restaurantSpendShareSettings) {
            this.editingReservation.restaurantSpendShareSettings = []
          }
          if (!this.editingReservation.reservationType) {
            this.editingReservation.reservationType = reservationType
          }

          this.myReservations.push(this.editingReservation)

          //console.log('Setting myReservations localStorage to ', this.myReservations)
          localforage.setItem('myReservations', JSON.stringify(this.myReservations))
        } catch (error) {
          console.error(error)
          reportError('reservations.store.js:newDraftReservation', error.message, null, null, null, error)
        }
      }
      else {
        //dummy reservation
        this.editingReservation = {
          'reservationType': reservationType,
          'date': '',
          'status': 'Draft',
          'userPermissions': [
            {
              'email': 'noone@nowhere.com',
              'permission': 'owner'
            }
          ],

          '_id': '123',

          'id': '123',
          'preferredRestaurant': restaurantName || null,
          'market': market || null,
          'requestExpirationTime': null,
          'restaurantSpendShareSettings': []
        }

      }
    },

    async viewReservation(idToView) {
      await this.getReservationById(idToView)
      this.viewingReservation = this.myReservations.find(({ id }) => id === idToView) || this.inReviewReservations.find(({ id }) => id === idToView) || this.completeReservations.find(({ id }) => id === idToView)
    },

    async cancelViewReservation() {
      this.viewingReservation = null
    },

    async editReservation(idToEdit) {
      console.log('EDITING ', idToEdit)
      await this.getReservationById(idToEdit)
      this.editingReservation = this.myReservations.find(({ id }) => id === idToEdit) || this.inReviewReservations.find(({ id }) => id === idToEdit) || this.completeReservations.find(({ id }) => id === idToEdit)
    },

    async openReservationDialogAtStep(reservationId, step = '8a') {
      this.openAtStep = step
      this.openAtPaymentStep = step === '8a'
      try {
        const res = await axios.get(`${baseUrl}${reservationId}`)
        // RM-400: a group/private dining inquiry has its own review step (5b); the
        // request's Review → Host Profile → Confirm & Pay sequence (7a/8a) is not it.
        if (res.data?.reservationType === 'private' && (step === '7a' || step === '8a')) {
          this.openAtStep = '5b'
          this.openAtPaymentStep = false
        }
        if (res.data) {
          const existing = this.myReservations.find(({ id }) => id === reservationId)
          if (!existing) {
            this.myReservations.push(res.data)
          } else {
            Object.assign(existing, res.data)
          }
          this.editingReservation = res.data
        }
      } catch (err) {
        console.error('Failed to open reservation dialog:', err)
        this.openAtStep = null
        this.openAtPaymentStep = false
      }
    },

    // RM-359: open a request the way tapping it on Bookings does, with what it has
    // filled in (the follow-up on an unfinished request links here), even before
    // Bookings has loaded the member's list. Only a request still in Draft opens;
    // anything else just stays on Bookings, where it is listed.
    async openRequest(reservationId) {
      try {
        const res = await axios.get(`${baseUrl}${reservationId}`)
        if (res.data?.status !== 'Draft')
          return
        const existing = this.myReservations.find(({ id }) => id === reservationId)
        if (!existing)
          this.myReservations.push(res.data)
        else
          Object.assign(existing, res.data)
        this.editingReservation = res.data
      }
      catch (err) {
        console.error('Failed to open the request:', err)
      }
    },

    async openReservationDialogAtPaymentStep(reservationId) {
      return this.openReservationDialogAtStep(reservationId, '8a')
    },

    async saveEditReservation(keepEditing) {
      this.saving = true
      try {
        let res = await axios.put(`${baseUrl}${this.editingReservation.id}`, this.editingReservation, {
          params: {
          }
        })

        // RM-359: a group/private submit held for RM approval → the notice for the next screen.
        if (res.data?.approvalNotice) {
          this.lastApprovalNotice = { reservationId: res.data._id || res.data.id, text: res.data.approvalNotice }
          delete res.data.approvalNotice
        }

        //Object.assign(this.editingReservation, res.data)
        let matchedMyReservation = this.myReservations.find(({ id }) => id === this.editingReservation.id)
        if (matchedMyReservation) Object.assign(matchedMyReservation, res.data)
        let inReviewReservation = this?.inReviewReservations?.find(({ id }) => id === this.editingReservation.id)
        if (inReviewReservation) Object.assign(inReviewReservation, res.data)
        let completeReservation = this?.completeReservations?.find(({ id }) => id === this.editingReservation.id)
        if (completeReservation) Object.assign(completeReservation, res.data)

        if (!keepEditing) {
          //closes editing dialog
          console.log('Closing editing dialog')
          this.editingReservation = null
        }

        //console.log('Setting myReservations localStorage to ', this.myReservations)
        localforage.setItem('myReservations', JSON.stringify(this.myReservations))
        localforage.setItem('inReviewReservations', JSON.stringify(this.inReviewReservations))
        this.saving = false
      } catch (error) {
        this.saving = false
        console.log('Error 3f3f', error)
        reportError('reservations.store.js:saveEditReservation', error.message, null, null, null, error)
        console.error('There was an error saving the reservation. Possible network error, or someone else was editing this reservation at the same time. Please try again')
        await this.getReservationById(this.editingReservation.id)
      }
    },

    async confirmViewingReservation() {
      if (this.viewingReservation.status == 'Draft') {
        this.viewingReservation.newStatus = 'Submitted'
      }
      try {
        this.saving = true
        let res = await axios.put(`${baseUrl}${this.viewingReservation.id}`, this.viewingReservation, {
          params: {
          }
        })
        Object.assign(this.viewingReservation, res.data)
        this.viewingReservation = null

        //console.log('Setting myReservations localStorage to ', this.myReservations)
        localforage.setItem('myReservations', JSON.stringify(this.myReservations))
        this.saving = false
      } catch (error) {
        this.saving = false
        console.error(error)
        reportError('reservations.store.js:confirmViewingReservation', error.message, null, null, null, error)
      }

    },

    resetPaymentState() {
      this.confirmedPresence = false
    },

    async confirmEditingReservation(submittedBy, stripePaymentMethodId, saveCardForFuture = true) {
      try {
        console.log("RUNNING confirmEditingReservation: ", submittedBy, stripePaymentMethodId)
        this.saving = true
        if (this.editingReservation.status == 'Draft') {
          this.editingReservation.newStatus = 'Submitted'
        }
        if (submittedBy) {
          this.editingReservation.submittedBy = submittedBy
        }

        if (stripePaymentMethodId) {
          this.editingReservation.stripeHoldPaymentMethodId = stripePaymentMethodId
        }

        this.editingReservation.stripeSaveCardForFuture = saveCardForFuture


        console.log(this.editingReservation.stripeHoldPaymentIntentId, this.editingReservation.stripeHoldPaymentIntentId)
        let res = await axios.put(`${baseUrl}${this.editingReservation.id}`, this.editingReservation, {
          params: {
          }
        })
        // RM-359: held for RM approval → keep the notice for the next screen.
        const approvalNotice = res.data?.approvalNotice
        if (approvalNotice) {
          this.lastApprovalNotice = { reservationId: res.data._id || res.data.id, text: approvalNotice }
          delete res.data.approvalNotice
        }
        Object.assign(this.editingReservation, res.data)
        this.editingReservation = null

        //console.log('Setting myReservations localStorage to ', this.myReservations)
        localforage.setItem('myReservations', JSON.stringify(this.myReservations))
        this.saving = false
      } catch (error) {
        this.saving = false
        console.error("Backend error during confirmEditingReservation: ", error)
        reportError('reservations.store.js:confirmEditingReservation', error.message, null, null, null, error)
        // A failed submit (e.g. a declined hold) reverts the reservation to
        // Draft server-side. Re-sync the open copy so it picks up the reverted
        // status + fresh updatedAt; otherwise the stale updatedAt misses the
        // optimistic-concurrency match on retry and the resubmit silently no-ops.
        try {
          if (this.editingReservation?.id) {
            await this.getReservationById(this.editingReservation.id)
          }
        } catch (resyncError) {
          console.error('Failed to re-sync reservation after submit error:', resyncError)
        }
        throw error
      }
    },

    async cancelEditReservation() {
      if (localStorage.getItem('jwt')) {
        if (this.editingReservation.createdAt == this.editingReservation.updatedAt) {
          await this.deleteReservation(this.editingReservation.id)
        } else {
          this.getReservationById(this.editingReservation.id)
        }
      }
      this.editingReservation = null
    },

    // RM-359 Part B: ask RM for feedback on a draft before submitting. Never
    // submits; the draft stays as it is. Returns the member-facing message.
    async askRmForFeedback(reservationId, question = '') {
      const res = await axios.post(`${baseUrl}${reservationId}/ask-rm-feedback`, { question })

      return res.data?.message
    },

    async deleteEditReservation() {
      await this.deleteReservation(this.editingReservation.id)
      this.editingReservation = null
    },

    async submitDraftReservation() {
      if (this.editingReservation.status == 'Draft') {
        this.editingReservation.newStatus = 'Submitted'
      }
      this.saveEditReservation()
    },

    async deleteReservation(idToDelete) {
      //let reservation = this.myReservations.find(({ id }) => id === idToDelete) || this.inReviewReservations.find(({ id }) => id === idToDelete) || this.completeReservations.find(({ id }) => id === idToDelete)
      this.deleteRefusal = null

      try {
        let res = await axios.delete(`${baseUrl}${idToDelete}`, {
          params: {
          }
        })

        //remove matching item from array
        var removeIndex = this.myReservations.map(item => item.id).indexOf(idToDelete)
        ~removeIndex && this.myReservations.splice(removeIndex, 1)
        var removeIndex2 = this?.inReviewReservations?.map(item => item.id).indexOf(idToDelete)
        ~removeIndex2 && this?.inReviewReservations?.splice(removeIndex2, 1)
        var removeIndex3 = this?.completeReservations?.map(item => item.id).indexOf(idToDelete)
        ~removeIndex3 && this?.completeReservations?.splice(removeIndex3, 1)

        if (this.editingReservation?.id == idToDelete)
          this.editingReservation = null

        //this.editingReservation.status = res.data.status
        //this.editingReservation = res.data
        //Object.assign(reservation, res.data)

        //console.log('Setting myReservations localStorage to ', this.myReservations)
        localforage.setItem('myReservations', JSON.stringify(this.myReservations))
        localforage.setItem('inReviewReservations', JSON.stringify(this.inReviewReservations))
      } catch (error) {
        // The server refused (409): a hold Stripe did not confirm released, a charged hold, an
        // instant booking with a payment. Its sentence is what the admin needs to see.
        if (error?.response?.status === 409 && error.response.data?.error) {
          this.deleteRefusal = { id: idToDelete, message: error.response.data.error, code: error.response.data.code }
          
          return
        }
        console.error(error)
        reportError('reservations.store.js:deleteReservation', error.message, null, null, null, error)
      }
    },

    async setReservationStatus(idToEdit, statusToSet, cancellationReason = null) {
      let reservation = this?.myReservations?.find(({ id }) => id === idToEdit) || this?.inReviewReservations?.find(({ id }) => id === idToEdit) || this?.completeReservations?.find(({ id }) => id === idToEdit)
      reservation.newStatus = statusToSet

      // RM-164: when cancelling, persist the member's required reason so it can
      // be shared with the restaurant in the cancellation comms.
      if (cancellationReason != null) reservation.cancellationReason = cancellationReason

      try {
        let res = await axios.put(`${baseUrl}${reservation.id}`, reservation, {
          params: {
          }
        })
        let matchedMyReservation = this?.myReservations?.find(({ id }) => id === reservation.id)
        if (matchedMyReservation) Object.assign(matchedMyReservation, res.data)
        let inReviewReservation = this?.inReviewReservations?.find(({ id }) => id === reservation.id)
        if (inReviewReservation) Object.assign(inReviewReservation, res.data)
        let completeReservation = this?.completeReservations?.find(({ id }) => id === reservation.id)
        if (completeReservation) Object.assign(completeReservation, res.data)

        localforage.setItem('myReservations', JSON.stringify(this.myReservations))
        localforage.setItem('inReviewReservations', JSON.stringify(this.inReviewReservations))
      } catch (error) {
        console.error(error)
        reportError('reservations.store.js:setReservationStatus', error.message, null, null, null, error)
      }
    },

    // RM-164: within-24h "request to cancel". Does NOT change the reservation
    // status (stays Upcoming) — it records the member's reason + a request
    // marker and fires the restaurant decision email + RM internal alert.
    // RM resolves the actual cancellation/hold manually.
    async requestCancellation(reservationId, cancellationReason) {
      const res = await axios.post(
        `${baseUrl}${reservationId}/cancellation-request`,
        { cancellationReason },
        {
          params: {
          }
        }
      )
      this.syncUpdatedReservation(res.data)
      localforage.setItem('myReservations', JSON.stringify(this.myReservations))
      localforage.setItem('inReviewReservations', JSON.stringify(this.inReviewReservations))

      return res.data
    },

    syncUpdatedReservation(updatedReservation) {
      const reservationId = updatedReservation?.id || updatedReservation?._id
      if (!reservationId) return
      const matchesReservation = item => item?.id === reservationId || item?._id === reservationId

      const matchedMyReservation = this.myReservations.find(matchesReservation)
      if (matchedMyReservation) Object.assign(matchedMyReservation, updatedReservation)

      const matchedInReviewReservation = this.inReviewReservations.find(matchesReservation)
      if (matchedInReviewReservation) Object.assign(matchedInReviewReservation, updatedReservation)

      const matchedCompleteReservation = this.completeReservations.find(matchesReservation)
      if (matchedCompleteReservation) Object.assign(matchedCompleteReservation, updatedReservation)

      if (this.editingReservation && matchesReservation(this.editingReservation)) {
        Object.assign(this.editingReservation, updatedReservation)
      }
      if (this.viewingReservation && matchesReservation(this.viewingReservation)) {
        Object.assign(this.viewingReservation, updatedReservation)
      }
    },

    // RM-400 — "Booked?": the member says where their group/private dining inquiry ended up
    // (restaurant, time, the signed contract or final receipt). The inquiry then reads
    // "Booked, pending RM approval" until RM approves it (approveInquiryBooking).
    async markInquiryBooked(reservationId, { restaurant, time, mediaIds }) {
      try {
        const res = await axios.post(`${baseUrl}${reservationId}/booked`, { restaurant, time, mediaIds })
        this.syncUpdatedReservation(res.data)
        localforage.setItem('myReservations', JSON.stringify(this.myReservations))
        localforage.setItem('inReviewReservations', JSON.stringify(this.inReviewReservations))
        
        return res.data
      } catch (error) {
        console.error(error)
        reportError('reservations.store.js:markInquiryBooked', error.message, null, null, null, error)
        throw error
      }
    },

    async approveInquiryBooking(reservationId) {
      try {
        const res = await axios.post(`${baseUrl}${reservationId}/approve-booking`, {})
        this.syncUpdatedReservation(res.data)
        localforage.setItem('myReservations', JSON.stringify(this.myReservations))
        localforage.setItem('inReviewReservations', JSON.stringify(this.inReviewReservations))
        
        return res.data
      } catch (error) {
        console.error(error)
        reportError('reservations.store.js:approveInquiryBooking', error.message, null, null, null, error)
        throw error
      }
    },

    async addReceiptImageToReservation(reservationId, imageId) {
      try {
        const res = await axios.post(
          `${baseUrl}${reservationId}/receipt`,
          { imageId },
          {
            params: {
            }
          }
        )
        this.syncUpdatedReservation(res.data)
        localforage.setItem('myReservations', JSON.stringify(this.myReservations))
        localforage.setItem('inReviewReservations', JSON.stringify(this.inReviewReservations))
      } catch (error) {
        console.error(error)
        reportError('reservations.store.js:addReceiptImageToReservation', error.message, null, null, null, error)
        throw error
      }
    },

    async removeReceiptImageFromReservation(reservationId, imageId) {
      try {
        const res = await axios.delete(
          `${baseUrl}${reservationId}/receipt/${imageId}`,
          {
            params: {
            }
          }
        )
        this.syncUpdatedReservation(res.data)
        localforage.setItem('myReservations', JSON.stringify(this.myReservations))
        localforage.setItem('inReviewReservations', JSON.stringify(this.inReviewReservations))
      } catch (error) {
        console.error(error)
        reportError('reservations.store.js:removeReceiptImageFromReservation', error.message, null, null, null, error)
        throw error
      }
    },

    async duplicateReservationAndEdit(idToDuplicate) {
      console.log('DUPLICATING ', idToDuplicate)
      let reservation = this.myReservations.find(({ id }) => id === idToDuplicate)
      let copy = JSON.parse(JSON.stringify(reservation))
      delete copy._id
      delete copy.id
      delete copy.owner
      delete copy.createdAt
      delete copy.updatedAt

      // delete copy.alternateRestaurants
      delete copy.confirmedRestaurant
      delete copy.confirmedTime
      delete copy.confirmedMessageToRm
      delete copy.confirmedMessageToMember
      delete copy.confirmationUuids
      delete copy.declines
      delete copy.submittedAt
      delete copy.acceptedAt
      delete copy.declinedAt
      copy.status = 'Draft'

      try {
        let res = await axios.post(`${baseUrl}`, copy, {
          params: {
          }
        })
        this.myReservations.push(res.data)
        this.editingReservation = res.data

        //console.log('Setting myReservations localStorage to ', this.myReservations)
        localforage.setItem('myReservations', JSON.stringify(this.myReservations))
      } catch (error) {
        console.error(error)
        reportError('reservations.store.js:duplicateReservationAndEdit', error.message, null, null, null, error)
      }
    },

    // Safe method to handle reservation cancellation
    async cancelReservation(item) {
      try {
        console.log('Setting cancelingReservation to:', item)
        this.cancelingReservation = item
      } catch (error) {
        console.error(error)
        reportError('reservations.store.js:cancelReservation', error.message, null, null, null, error)
      }
    },

    async fetchInsights() {
      if (this.insightsLoading) return
      this.insightsLoading = true
      try {
        const res = await axios.get(`${baseUrl}insights`)
        this.insights = res.data
      } catch (error) {
        console.error(error)
        reportError('reservations.store.js:fetchInsights', error.message, null, null, null, error)
      } finally {
        this.insightsLoading = false
      }
    },
  }
})
